import {
  Home,
  Building2,
  Server,
  Mail,
  Cookie,
  UserCheck,
  RefreshCw,
  BellRing,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  LegalPage,
  LegalSection,
  LegalHeading,
  LegalList,
  Placeholder,
} from "@/components/blocks/LegalPage";
import {
  COMPANY,
  SUPERVISORY_AUTHORITY,
  HOSTING_PROVIDER,
  LOGFILE_RETENTION,
} from "@/lib/legal";
import { CONTACT_EMAIL, CONTACT_PHONE, ROUTE_WAITLIST } from "@/lib/brand";

const SIDEBAR_ITEMS = [
  { id: "top", label: "Start", icon: Home },
  { id: "verantwortlicher", label: "Verantwortlicher", icon: Building2 },
  { id: "aufruf", label: "Aufruf der Website", icon: Server },
  { id: "kontakt", label: "Kontaktaufnahme", icon: Mail },
  { id: "warteliste", label: "Warteliste", icon: BellRing },
  { id: "cookies", label: "Cookies", icon: Cookie },
  { id: "rechte", label: "Eure Rechte", icon: UserCheck },
  { id: "aenderungen", label: "Änderungen", icon: RefreshCw },
];

const Wert = ({ children }) =>
  typeof children === "string" && children.startsWith("TODO") ? (
    <Placeholder>{children}</Placeholder>
  ) : (
    <>{children}</>
  );

const linkClass =
  "text-white/80 underline decoration-white/25 underline-offset-4 hover:text-white hover:decoration-brand-orange transition-colors duration-300";

export default function Privacy() {
  return (
    <LegalPage
      eyebrow="Rechtliches"
      title="Datenschutz"
      lead="Diese Erklärung beschreibt, welche Daten beim Besuch dieser Website verarbeitet werden. Sie gilt für den öffentlichen Auftritt - für die Plattform im angemeldeten Zustand folgt eine eigene, deutlich umfangreichere Fassung."
      sidebarItems={SIDEBAR_ITEMS}
    >
      <LegalSection id="verantwortlicher" title="Verantwortlicher">
        <p>
          MatchGate ist ein Produkt von NxGn Media. Verantwortlich für die
          Datenverarbeitung auf dieser Website im Sinne der
          Datenschutz-Grundverordnung ist:
        </p>
        <p>
          {COMPANY.name}
          <br />
          {COMPANY.street}
          <br />
          {COMPANY.postalCode} {COMPANY.city}
          <br />
          <br />
          Telefon: {CONTACT_PHONE}
          <br />
          E-Mail:{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className={linkClass}>
            {CONTACT_EMAIL}
          </a>
        </p>
        <p>
          Die vollständigen Angaben stehen im{" "}
          <Link to="/impressum" className={linkClass}>
            Impressum
          </Link>
          . Einen Datenschutzbeauftragten müssen wir nicht benennen, da die
          gesetzlichen Voraussetzungen dafür nicht vorliegen.
        </p>
      </LegalSection>

      <LegalSection id="aufruf" title="Aufruf der Website">
        <p>
          Beim Aufruf dieser Website überträgt euer Browser technisch
          notwendige Daten an unseren Hosting-Anbieter, die dort in sogenannten
          Server-Logfiles gespeichert werden:
        </p>
        <LegalList
          items={[
            "IP-Adresse des anfragenden Geräts",
            "Datum und Uhrzeit des Zugriffs",
            "Name und URL der abgerufenen Datei",
            "Übertragene Datenmenge und Meldung über den Abruferfolg",
            "Verwendeter Browser samt Version und Betriebssystem",
            "Zuvor besuchte Seite, sofern der Browser sie übermittelt",
          ]}
        />
        <p>
          Diese Verarbeitung ist erforderlich, um die Website auszuliefern,
          ihre Stabilität zu gewährleisten und Angriffe nachvollziehen zu
          können. Rechtsgrundlage ist Artikel 6 Absatz 1 Buchstabe f DSGVO
          (berechtigtes Interesse an einem sicheren und funktionsfähigen
          Angebot). Eine Zusammenführung dieser Daten mit anderen Datenquellen
          findet nicht statt.
        </p>
        <p>
          Hosting-Anbieter: <Wert>{HOSTING_PROVIDER}</Wert>. Mit diesem Anbieter
          besteht ein Vertrag zur Auftragsverarbeitung nach Artikel 28 DSGVO.
          Server-Logfiles löschen wir nach spätestens {LOGFILE_RETENTION}.
        </p>
        <p>
          Schriften und Bilder liefern wir selbst aus. Beim Aufruf wird keine
          Verbindung zu Servern Dritter aufgebaut, es sind keine externen
          Dienste eingebunden.
        </p>
      </LegalSection>

      <LegalSection id="kontakt" title="Kontaktaufnahme">
        <p>
          Auf der Kontaktseite steht ein Formular bereit. Beim Absenden
          überträgt euer Browser die Angaben (Anliegen, ob ihr als Verein,
          Unternehmen oder sonstige Organisation schreibt, Name, Verein oder
          Unternehmen, E-Mail-Adresse und Nachricht) an unseren Server beim
          oben genannten Hosting-Anbieter. Ein Skript leitet sie von dort per
          E-Mail an {CONTACT_EMAIL} weiter. Auf dem Server selbst werden sie
          nicht gespeichert.
        </p>
        <p>
          Zum Schutz vor automatisierten Spam-Nachrichten enthält das Formular
          ein für Menschen unsichtbares Feld und misst, wie lange es bis zum
          Absenden geöffnet war. Diese Angaben werten wir nur beim Absenden aus
          und speichern sie nicht. Es werden keine Dienste Dritter, kein
          Captcha und keine Cookies eingesetzt.
        </p>
        <p>
          Wenn ihr uns per E-Mail oder Telefon kontaktiert, verarbeiten wir die
          von euch mitgeteilten Angaben, um die Anfrage zu beantworten.
          Rechtsgrundlage ist Artikel 6 Absatz 1 Buchstabe b DSGVO, sofern die
          Anfrage auf einen Vertrag abzielt, sonst Artikel 6 Absatz 1 Buchstabe
          f DSGVO (berechtigtes Interesse an der Beantwortung).
        </p>
        <p>
          Wir löschen diese Daten, sobald die Anfrage abschließend bearbeitet
          ist und keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
        </p>
      </LegalSection>

      <LegalSection id="warteliste" title="Warteliste">
        <p>
          Auf der Seite{" "}
          <Link to={ROUTE_WAITLIST} className={linkClass}>
            Warteliste
          </Link>{" "}
          könnt ihr euch für den Start der Plattform vormerken. Die Angaben
          gehen wie beim Kontaktformular an unseren Server, mit demselben Schutz
          vor Spam.
        </p>
        <p>
          <strong className="text-white/80">Bestätigung per E-Mail
          (Double-Opt-In):</strong> Nach dem Absenden schicken wir euch eine
          E-Mail mit einem Bestätigungslink. Eure Angaben stecken, gegen
          Veränderung gesichert, in diesem Link und werden bis dahin nirgends
          gespeichert.
          Erst wenn ihr den Link öffnet und den Eintrag bestätigt, erreichen uns
          die Angaben per E-Mail, zusammen mit dem Zeitpunkt der Eintragung und
          der Bestätigung. Das dient dem Nachweis eurer Einwilligung. Bestätigt
          ihr nicht, verfällt der Link nach sieben Tagen, und es bleibt nichts
          bei uns.
        </p>
        <p>
          Wir verarbeiten dabei die Angaben aus dem Formular: Rolle (Verein
          oder Unternehmen), Name, Name des Vereins oder Unternehmens,
          Postleitzahl oder Ort, E-Mail-Adresse und ob ihr MatchGate vor dem
          Start testen möchtet. Wir nutzen sie, um euch per E-Mail über den
          Start zu benachrichtigen, den Einstieg mit euch vorzubereiten und,
          wenn ihr das angekreuzt habt, euch für einen Test vor dem Start
          anzusprechen. Rechtsgrundlage ist eure Einwilligung nach Artikel 6
          Absatz 1 Buchstabe a DSGVO.
        </p>
        <p>
          Ihr könnt die Einwilligung jederzeit mit Wirkung für die Zukunft
          widerrufen, eine kurze Nachricht an{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className={linkClass}>
            {CONTACT_EMAIL}
          </a>{" "}
          genügt. Die Rechtmäßigkeit der Verarbeitung bis zum Widerruf bleibt
          davon unberührt.
        </p>
        <p>
          Wir löschen eure Angaben, sobald ihr widerruft, spätestens aber sechs
          Monate nach dem Start der Plattform, wenn ihr bis dahin kein Konto
          angelegt habt. Den Versand der E-Mails übernimmt unser Hosting-Anbieter
          als Auftragsverarbeiter; darüber hinaus findet keine Weitergabe an
          Dritte statt.
        </p>
      </LegalSection>

      <LegalSection id="cookies" title="Cookies und Reichweitenmessung">
        <p>
          Diese Website setzt{" "}
          <strong className="text-white/80">keine Cookies</strong>. Es findet
          keine Analyse des Nutzungsverhaltens statt, es sind keine
          Tracking-Dienste, keine Werbenetzwerke und keine sozialen Netzwerke
          eingebunden. Auch der lokale Browser-Speicher wird nicht verwendet.
        </p>
        <p>
          Eine Einwilligungsabfrage („Cookie-Banner") ist deshalb derzeit nicht
          erforderlich.
        </p>
        <p>
          Mit dem Start der Plattform kommen technisch notwendige Cookies
          hinzu, etwa für die Anmeldung. Welche das voraussichtlich sind, steht
          in der{" "}
          <Link to="/cookies" className={linkClass}>
            Cookie-Übersicht
          </Link>
          . Wir passen diese Erklärung vorher an.
        </p>
      </LegalSection>

      <LegalSection id="rechte" title="Eure Rechte">
        <p>
          Ihr habt uns gegenüber jederzeit die folgenden Rechte hinsichtlich
          eurer personenbezogenen Daten:
        </p>
        <LegalList
          items={[
            "Auskunft über die verarbeiteten Daten (Artikel 15 DSGVO)",
            "Berichtigung unrichtiger Daten (Artikel 16 DSGVO)",
            "Löschung (Artikel 17 DSGVO)",
            "Einschränkung der Verarbeitung (Artikel 18 DSGVO)",
            "Datenübertragbarkeit (Artikel 20 DSGVO)",
            "Widerspruch gegen die Verarbeitung (Artikel 21 DSGVO)",
            "Widerruf einer erteilten Einwilligung (Artikel 7 Absatz 3 DSGVO)",
          ]}
        />
        <p>
          Für alle diese Anliegen genügt eine Nachricht an{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className={linkClass}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>

        <LegalHeading>Widerspruchsrecht</LegalHeading>
        <p>
          Soweit wir Daten auf Grundlage eines berechtigten Interesses nach
          Artikel 6 Absatz 1 Buchstabe f DSGVO verarbeiten, könnt ihr dieser
          Verarbeitung aus Gründen widersprechen, die sich aus eurer besonderen
          Situation ergeben. Wir verarbeiten die Daten dann nicht mehr, es sei
          denn, wir können zwingende schutzwürdige Gründe nachweisen.
        </p>

        <LegalHeading>Beschwerde bei einer Aufsichtsbehörde</LegalHeading>
        <p>
          Unabhängig davon steht euch nach Artikel 77 DSGVO ein Beschwerderecht
          bei einer Datenschutz-Aufsichtsbehörde zu. Für uns zuständig ist{" "}
          {SUPERVISORY_AUTHORITY}.
        </p>
      </LegalSection>

      <LegalSection id="aenderungen" title="Änderungen dieser Erklärung">
        <p>
          Wir passen diese Datenschutzerklärung an, sobald sich die
          Datenverarbeitung ändert - etwa durch neue Funktionen oder
          eingebundene Dienste. Es gilt jeweils die hier veröffentlichte
          Fassung. Der Stand ist oben auf der Seite vermerkt.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
